/** @file Snaps a world point to the nearest grid point: square tile corners or iso diamond corners. */
import { vec2 } from '@pose2d/math/vec2';
import type { Vec2 } from '@pose2d/math/types';
import { gridTileSize } from './gridTileSize';
import type { GridView } from './gridTileSize';

/** The integer on the other side of f from its rounding r (the second-nearest integer). */
function otherNeighbour(f: number, r: number): number {
  return f >= r ? r + 1 : r - 1;
}

/** Nearest point of the lattice { (i * hw, j * hh) : i + j even } to (x, y). */
function snapIso(x: number, y: number, hw: number, hh: number, out: Vec2): Vec2 {
  const fi = x / hw;
  const fj = y / hh;
  const i = Math.round(fi);
  const j = Math.round(fj);
  if ((i + j) % 2 === 0) return vec2.set(out, i * hw, j * hh);
  // Odd parity: move exactly one coordinate to its other neighbour, whichever adds less squared distance.
  const i2 = otherNeighbour(fi, i);
  const j2 = otherNeighbour(fj, j);
  const costI = ((i2 - fi) * hw) ** 2 - ((i - fi) * hw) ** 2;
  const costJ = ((j2 - fj) * hh) ** 2 - ((j - fj) * hh) ** 2;
  return costI <= costJ ? vec2.set(out, i2 * hw, j * hh) : vec2.set(out, i * hw, j2 * hh);
}

/** Writes the grid point nearest to (x, y) into out (unchanged when off or a tile size <= 0); origin = world origin. */
export function snapPoint(grid: GridView, x: number, y: number, out: Vec2): Vec2 {
  if (grid.mode === 'off') return vec2.set(out, x, y);
  const [w, h] = gridTileSize(grid);
  if (!(w > 0 && h > 0)) return vec2.set(out, x, y);
  if (grid.mode === 'square') return vec2.set(out, Math.round(x / w) * w, Math.round(y / h) * h);
  return snapIso(x, y, w / 2, h / 2, out);
}
